import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, styled, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Typography, CircularProgress, Divider } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Home as HomeIcon,
  BarChart as BarChartIcon,
  TrackChanges as TargetIcon,
  AutoAwesome as SparklesIcon,
  ChatBubbleOutline as MessageSquareIcon,
  FlashOn as ZapIcon,
  Article as ArticleIcon,
  Settings as SettingsIcon,
  Security as ShieldIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Check as CheckIcon,
  Logout as LogoutIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { useTheme } from '../../globalContexts/ThemeContext';
import { userProfile, handleLogout } from 'utils/auth';
import { SERVER_PROFILE_UPDATED_EVENT } from 'utils/serverProfileCache';
import logoBlackImage from '../../assets/images/logo_black.png';
import logoWhiteImage from '../../assets/images/logo_white.png';
import { selectDealerSelected, selectDealersList } from '../../redux/headerSlice/selectors';
import { headerActions } from '../../redux/headerSlice/slice';
import DealerService from '../../services/apiService/dealerApiService';
import { getMaterialIconComponent } from 'app/components/MaterialIconMap';
import { DebugPanelMAIQ } from 'app/components/ReusableUIComponents/HighLevelComponents/DebugPanelMAIQ';
import { shouldShowMockIndicator } from 'app/apiService/miaChat/config';
import { DealerVersionLabel } from 'app/components/ReusableUIComponents/LowLevelComponents/DealerVersionLabel';
import { hasResource } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import { AgentTypeDropdown } from 'app/components/ReusableUIComponents/AgentTypeDropdown';

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 256,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
}));

const DealershipSelector = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DealerLogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 80,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const DealerLogoImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

const DealershipButton = styled(Button)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Navigation = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  justifyContent: 'flex-start',
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',
  ...(active
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
      }
    : {
        color: theme.palette.text.secondary,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
      }),
}));

const AdminSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const ProfileSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ProfileButton = styled(Button)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  justifyContent: 'flex-start',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1rem',
  fontWeight: 600,
}));

const UserInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
  textAlign: 'left',
}));

const UserRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.2,
  textAlign: 'left',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'center',
}));

const LogoImage = styled('img')({
  width: '100%',
  height: 50,
  objectFit: 'contain',
});

// Type for navigation items from API
type NavigationItemFromAPI = {
  label: string;
  link: string;
  icon: string;
};

// Type for navigation items in component
type NavigationItem = {
  name: string;
  icon: React.ComponentType<any>;
  href: string;
};

const normalizeAIAvatarLink = (link?: string): string => {
  const normalizedLink = (link || '').trim();
  if (!normalizedLink) return '#';
  if (normalizedLink.startsWith('/ai-avatar-layout')) return normalizedLink;
  if (normalizedLink.startsWith('http://') || normalizedLink.startsWith('https://')) return normalizedLink;
  if (normalizedLink.startsWith('#')) return normalizedLink;
  if (normalizedLink.startsWith('/')) return `/ai-avatar-layout${normalizedLink}`;
  return `/ai-avatar-layout/${normalizedLink}`;
};

// Function to get sidebar navigation mappings from user_token
function getSidebarNavigationMappings(): NavigationItemFromAPI[] {
  try {
    const user = userProfile(false);
    const mappings = user?.sidebar_navigation_mappings || [];
    return Array.isArray(mappings) ? mappings : [];
  } catch (error) {
    console.error('Error reading sidebar_navigation_mappings from trusted profile:', error);
    return [];
  }
}

// Function to check if an item is an admin item (Settings or Admin)
function isAdminItem(link: string): boolean {
  const normalizedLink = link?.toLowerCase().trim() || '';
  return normalizedLink.includes('/settings') || normalizedLink.includes('/admin') || normalizedLink.includes('/feedback');
}

// Function to map API navigation items to component navigation items
function mapNavigationItems(apiItems: NavigationItemFromAPI[]): NavigationItem[] {
  return apiItems.map((item) => {
    // Get icon component from MaterialIconMap
    const iconElement = getMaterialIconComponent(item.icon);
    
    // Create a wrapper component that renders the icon element
    // If icon not found, use HomeIcon as fallback
    const IconComponent: React.ComponentType<any> = iconElement 
      ? (props: any) => React.cloneElement(iconElement, props)
      : HomeIcon;
    
    return {
      name: item.label || 'Unknown',
      icon: IconComponent,
      href: normalizeAIAvatarLink(item.link),
    };
  });
}

// Function to get admin items from API (Settings and Admin)
function getAdminItemsFromAPI(): NavigationItem[] {
  try {
    const apiItems = getSidebarNavigationMappings();
    if (apiItems.length === 0) {
      return [];
    }
    
    // Filter only admin items (Settings and Admin)
    const adminApiItems = apiItems.filter(item => isAdminItem(item.link || ''));
    
    // Map to navigation items
    return mapNavigationItems(adminApiItems);
  } catch (error) {
    console.error('Error getting admin items from API:', error);
    return [];
  }
}

// Function to get main navigation items (excluding admin items)
function getMainNavigationItems(apiItems: NavigationItemFromAPI[]): NavigationItem[] {
  // Filter out admin items (Settings and Admin)
  const mainItems = apiItems.filter(item => !isAdminItem(item.link || ''));
  return mapNavigationItems(mainItems);
}

// Fallback navigation items if no mappings found
const fallbackNavigationItems: NavigationItem[] = [
  { name: 'Home', icon: HomeIcon, href: '/ai-avatar-layout' },
  { name: 'Measure', icon: BarChartIcon, href: '/ai-avatar-layout/measure' },
  { name: 'Target', icon: TargetIcon, href: '/ai-avatar-layout/target' },
  { name: 'Generate', icon: SparklesIcon, href: '/ai-avatar-layout/generate' },
  { name: 'Analyze', icon: MessageSquareIcon, href: '/ai-avatar-layout/analyze' },
];

// Get initial navigation items from trusted profile (excluding admin items)
const getInitialNavigationItems = (): NavigationItem[] => {
  const apiNavigationItems = getSidebarNavigationMappings();
  return apiNavigationItems.length > 0 
    ? getMainNavigationItems(apiNavigationItems)
    : fallbackNavigationItems;
};

// Get initial admin items from trusted profile
const getInitialAdminItems = (): NavigationItem[] => {
  return getAdminItemsFromAPI();
};

// Shareable Nurturing History route – do not overwrite dealer selection when sidebar mounts
const isShareableNurturingHistoryRoute = (pathname: string) => pathname.startsWith('/url/');

export const Sidebar: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { mode, toggleTheme } = useTheme();
  const isShareableHistoryPage = isShareableNurturingHistoryRoute(location.pathname);

  // Get dealers list and selected dealer from Redux
  const dealersList = useSelector(selectDealersList);
  const selectedDealer = useSelector(selectDealerSelected);
  
  const [dealerMenuAnchor, setDealerMenuAnchor] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [dynamicNavigationItems, setDynamicNavigationItems] = useState<NavigationItem[]>(getInitialNavigationItems());
  const [dynamicAdminItems, setDynamicAdminItems] = useState<NavigationItem[]>(getInitialAdminItems());
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const canAccessDebugPanel = hasResource('DebugPanelMAIQ');
  const dealerMenuOpen = Boolean(dealerMenuAnchor);
  const menuOpen = Boolean(anchorEl);

  // Refs to prevent duplicate dealers API calls (state updates re-trigger effect; Strict Mode double-mount)
  const dealersLoadedRef = useRef(false);
  const fetchDealersInProgressRef = useRef(false);
  const dealersListRef = useRef(dealersList);
  const localStorageSyncDoneRef = useRef(false);
  dealersListRef.current = dealersList;
  
  useEffect(() => {
    const updateNavigationItems = () => {
      try {
        const apiItems = getSidebarNavigationMappings();
        if (apiItems.length > 0) {
          const mainItems = getMainNavigationItems(apiItems);
          setDynamicNavigationItems(mainItems);
          const adminItems = getAdminItemsFromAPI();
          setDynamicAdminItems(adminItems);
        } else {
          setDynamicNavigationItems(fallbackNavigationItems);
          setDynamicAdminItems([]);
        }
      } catch (error) {
        console.error('Error updating navigation items:', error);
        setDynamicNavigationItems(fallbackNavigationItems);
        setDynamicAdminItems([]);
      }
    };

    updateNavigationItems();

    window.addEventListener(SERVER_PROFILE_UPDATED_EVENT, updateNavigationItems);

    return () => {
      window.removeEventListener(SERVER_PROFILE_UPDATED_EVENT, updateNavigationItems);
    };
  }, []);

  const user = userProfile(false);
  const userName = user?.name || user?.email || 'User';
  const userInitials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  // Function to format role name for display
  const formatRoleName = (role: string | string[] | undefined): string => {
    // Handle undefined or empty
    if (!role) return 'User';
    
    // Handle array - take the first role
    let roleString: string;
    if (Array.isArray(role)) {
      if (role.length === 0) return 'User';
      roleString = role[0];
    } else {
      roleString = role;
    }
    
    // Role mapping for common roles
    const roleDisplayMap: Record<string, string> = {
      'beta_maiq_performance_manager': 'Performance Manager',
      'maiq_performance_manager': 'Performance Manager',
      'dealership_executive': 'Dealership Executive',
      'dealership_group_executive': 'Dealership Group Executive',
      'maiq_super_admin': 'Super Admin',
      'super_user': 'Super User',
      'maiq_tech_user': 'Tech User',
      'maiq_sales_user': 'Sales User',
      'sales_person': 'Sales Person',
      'maiq_content_manager': 'Content Manager',
    };
    
    // Check if role exists in mapping
    if (roleDisplayMap[roleString]) {
      return roleDisplayMap[roleString];
    }
    
    // Fallback: convert snake_case to Title Case
    return roleString
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Get user role from trusted profile
  const userRole = formatRoleName(user?.roles);
  
  // Get current dealership name - use first dealer if selectedDealer is 'default' or not available
  const getCurrentDealerName = () => {
    // If selectedDealer is a valid dealer object
    if (selectedDealer && typeof selectedDealer === 'object' && selectedDealer !== null) {
      return selectedDealer?.site_title || selectedDealer?.name || '';
    }
    
    // If selectedDealer is 'default' or not set, use first dealer from list
    if (dealersList?.data?.body?.items?.length > 0) {
      const firstDealer = dealersList.data.body.items[0];
      return firstDealer?.site_title || firstDealer?.name || '';
    }
    
    return '';
  };
  
  const currentDealerName = getCurrentDealerName();

  const currentDealerVersion = (() => {
    if (selectedDealer && typeof selectedDealer === 'object' && selectedDealer !== null) {
      return selectedDealer?.version;
    }
    if (dealersList?.data?.body?.items?.length > 0) {
      const firstDealer = dealersList.data.body.items[0];
      return firstDealer?.version;
    }
    return null;
  })();

  const currentDealerStatus = (() => {
    if (selectedDealer && typeof selectedDealer === 'object' && selectedDealer !== null) {
      return selectedDealer?.status;
    }
    if (dealersList?.data?.body?.items?.length > 0) {
      const firstDealer = dealersList.data.body.items[0];
      return firstDealer?.status;
    }
    return null;
  })();
  
  // Fetch dealers once on mount; refs prevent duplicate calls when state updates or Strict Mode re-mounts
  useEffect(() => {
    // On Shareable Nurturing History page, preserve existing dealer – do not fetch or overwrite if we already have dealers
    if (isShareableHistoryPage && (dealersListRef.current?.data?.body?.items?.length ?? 0) > 0) {
      return;
    }
    // Already loaded (e.g. from Redux after previous fetch or saga)
    if (dealersLoadedRef.current || (dealersListRef.current?.data?.body?.items?.length ?? 0) > 0) {
      return;
    }
    // Another fetch already in progress (e.g. Strict Mode double-mount)
    if (fetchDealersInProgressRef.current) {
      return;
    }

    const fetchDealers = async () => {
      fetchDealersInProgressRef.current = true;
      setLoadingDealers(true);
      try {
        const response = await DealerService.getAllDealers();
        if (response.body?.items) {
          // Sort dealers: active first, then alphabetically
          const sortedDealers = response.body.items
            .map((item: any) => ({
              ...item,
              dealer: item._id || "",
              site_title: item.name || ""
            }))
            .sort((a: any, b: any) => {
              if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') {
                return 1;
              } else if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') {
                return -1;
              } else {
                return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
              }
            });

          dispatch(headerActions.requestDealersSuccess({
            ...response,
            body: { ...response.body, items: sortedDealers }
          }));
          dealersLoadedRef.current = true;

          // On Shareable Nurturing History page, never overwrite dealer selection
          if (isShareableHistoryPage) {
            return;
          }
          // Auto-select first dealer if none selected (on mount selectedDealer is typically 'default')
          if (sortedDealers.length > 0) {
            const isSelectedDealerValid =
              selectedDealer &&
              typeof selectedDealer === 'object' &&
              selectedDealer !== null &&
              ((selectedDealer as any)._id || (selectedDealer as any).dealer);

            if (!isSelectedDealerValid || selectedDealer === 'default') {
              const firstDealer = sortedDealers[0];
              dispatch(headerActions.selectDealer(firstDealer));
              localStorage.setItem('dealer', JSON.stringify(firstDealer));
            }
          }
        }
      } catch (error) {
        console.error('Error loading dealers:', error);
        dealersLoadedRef.current = false;
      } finally {
        fetchDealersInProgressRef.current = false;
        setLoadingDealers(false);
      }
    };

    fetchDealers();
    // Intentionally only dispatch + isShareableHistoryPage: avoid re-running when dealersList/selectedDealer change after our own dispatch
  }, [dispatch, isShareableHistoryPage]);

  // Sync dealer from localStorage once when dealers list is first loaded (avoids re-running on every selectedDealer change)
  useEffect(() => {
    if (!dealersList?.data?.body?.items?.length || loadingDealers) return;
    if (localStorageSyncDoneRef.current) return;

    const handleDealerFromLocalStorage = () => {
      try {
        // On Shareable Nurturing History page, preserve existing dealer – do not overwrite selection
        if (isShareableHistoryPage) {
          localStorageSyncDoneRef.current = true;
          return;
        }

        const isSelectedDealerValid =
          selectedDealer &&
          typeof selectedDealer === 'object' &&
          selectedDealer !== null &&
          (selectedDealer._id || selectedDealer.dealer);

        if (!isSelectedDealerValid || selectedDealer === 'default') {
          const firstDealer = dealersList!.data!.body!.items[0];
          if (firstDealer) {
            dispatch(headerActions.selectDealer(firstDealer));
            localStorage.setItem('dealer', JSON.stringify(firstDealer));
          }
          localStorageSyncDoneRef.current = true;
          return;
        }

        const storedDealer = localStorage.getItem('dealer');
        const dealer = storedDealer ? JSON.parse(storedDealer) : {};
        const currentDealerId =
          Object.keys(dealer).length ? dealer.dealer || dealer._id : 'default';
        const selectedDealerId = selectedDealer?._id || selectedDealer?.dealer;

        if (currentDealerId !== selectedDealerId && currentDealerId !== 'default') {
          const dealerFromStorage = dealersList.data.body.items.find(
            (item: any) => item._id === currentDealerId || item.dealer === currentDealerId
          );
          if (dealerFromStorage) {
            dispatch(headerActions.selectDealer(dealerFromStorage));
          } else {
            const firstDealer = dealersList.data.body.items[0];
            if (firstDealer) {
              dispatch(headerActions.selectDealer(firstDealer));
              localStorage.setItem('dealer', JSON.stringify(firstDealer));
            }
          }
        }
        localStorageSyncDoneRef.current = true;
      } catch (error) {
        console.error('Error loading dealer from localStorage:', error);
        if (!isShareableHistoryPage) {
          const firstDealer = dealersList.data.body.items[0];
          if (firstDealer) {
            dispatch(headerActions.selectDealer(firstDealer));
            localStorage.setItem('dealer', JSON.stringify(firstDealer));
          }
        }
        localStorageSyncDoneRef.current = true;
      }
    };

    handleDealerFromLocalStorage();
  }, [dispatch, dealersList, loadingDealers, selectedDealer, isShareableHistoryPage]);

  const isActive = (href: string) => {
    // For Unsold page, also check if we're on a lead details page
    if (href === '/ai-avatar-layout/unsold') {
      return location.pathname === href || location.pathname.startsWith('/ai-avatar-layout/unsold/lead/');
    }
    return location.pathname === href;
  };

  const handleNavigation = (href: string) => {
    history.push(href);
  };

  const handleDealerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDealerMenuAnchor(event.currentTarget);
  };

  const handleDealerMenuClose = () => {
    setDealerMenuAnchor(null);
  };

  const handleDealerSelect = (dealer: any) => {
    // Save to localStorage
    localStorage.setItem('dealer', JSON.stringify(dealer));
    // Update Redux state
    dispatch(headerActions.selectDealer(dealer));
    handleDealerMenuClose();
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (newMode: 'light' | 'dark') => {
    if (mode !== newMode) {
      toggleTheme();
    }
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    void handleLogout();
  };

  return (
    <SidebarContainer>
      {/* Dealership Selector */}
      <DealershipSelector>
        {/* Dealer Logo */}
        {selectedDealer?.logo_url && (
          <DealerLogoContainer key={selectedDealer._id || selectedDealer.dealer}>
            <DealerLogoImage 
              src={selectedDealer.logo_url} 
              alt={currentDealerName}
              onError={(e) => {
                // Hide image on error
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </DealerLogoContainer>
        )}
        
        <DealershipButton onClick={handleDealerMenuOpen} disabled={loadingDealers}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {loadingDealers ? (
              <CircularProgress size={20} />
            ) : (
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                {currentDealerName
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              <DealerVersionLabel
                dealerName={currentDealerName}
                dealerVersion={currentDealerVersion}
                dealerStatus={currentDealerStatus}
              />
            </Typography>
          </Box>
          <ChevronDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        </DealershipButton>
        
        {/* Dealers Menu */}
        <Menu
          anchorEl={dealerMenuAnchor}
          open={dealerMenuOpen}
          onClose={handleDealerMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 240,
              maxHeight: 400,
              mt: 1,
            },
          }}
        >
          {dealersList?.data?.body?.items?.map((dealer: any) => (
            <MenuItem
              key={dealer._id || dealer.dealer}
              onClick={() => handleDealerSelect(dealer)}
              selected={
                selectedDealer?.dealer === dealer._id || 
                selectedDealer?._id === dealer._id ||
                selectedDealer?.dealer === dealer.dealer
              }
              disabled={dealer.status !== 'ACTIVE'}
              sx={{
                opacity: dealer.status !== 'ACTIVE' ? 0.5 : 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                  {(dealer.name || dealer.site_title || 'D')
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <DealerVersionLabel
                      dealerName={dealer.name || dealer.site_title}
                      dealerVersion={dealer.version}
                    />
                  </Typography>
                }
                secondary={dealer.status !== 'ACTIVE' ? 'Inactive' : null}
              />
              {(selectedDealer?.dealer === dealer._id || 
                selectedDealer?._id === dealer._id ||
                selectedDealer?.dealer === dealer.dealer) && (
                <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />
              )}
            </MenuItem>
          ))}
          
          {(!dealersList?.data?.body?.items || dealersList.data.body.items.length === 0) && (
            <MenuItem disabled>
              <ListItemText primary="No dealers available" />
            </MenuItem>
          )}
        </Menu>

        {/* Agent Type filter — sits below the dealer selector and shares the
            same global selection logic as the header dropdown. Self-renders
            nothing when the user lacks the `AgentTypeDropdown` resource. */}
        <AgentTypeDropdown variant="sidebar" />
      </DealershipSelector>

      {/* Main Navigation */}
      <Navigation>
        {dynamicNavigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <NavButton
              key={item.name}
              active={active}
              onClick={() => handleNavigation(item.href)}
            >
              <Icon sx={{ fontSize: 20 }} />
              <span>{item.name}</span>
            </NavButton>
          );
        })}

        {/* Admin Section - Only show if admin items are available from API */}
        {dynamicAdminItems.length > 0 && (
        <AdminSection>
            {dynamicAdminItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <NavButton
                key={item.name}
                active={active}
                onClick={() => handleNavigation(item.href)}
              >
                <Icon sx={{ fontSize: 20 }} />
                <span>{item.name}</span>
              </NavButton>
            );
          })}
        </AdminSection>
        )}
      </Navigation>

      {/* Profile Section */}
      <ProfileSection>
        <ProfileButton onClick={handleProfileClick}>
          <UserAvatar>{userInitials}</UserAvatar>
          <UserInfo>
            <UserName>{userName}</UserName>
            <UserRole>{userRole}</UserRole>
          </UserInfo>
          <ChevronDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </ProfileButton>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 240,
              mt: -1,
              ml: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleThemeChange('light')}>
            <ListItemIcon>
              <LightModeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bright Mode</ListItemText>
            {mode === 'light' && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>
          <MenuItem onClick={() => handleThemeChange('dark')}>
            <ListItemIcon>
              <DarkModeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Dark Mode</ListItemText>
            {mode === 'dark' && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>
          {/* Debug Panel - Only show if enabled and user has role access */}
          {canAccessDebugPanel && (
            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={() => {
                setDebugPanelOpen(true);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <BugReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Debug Panel</ListItemText>
              </MenuItem>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={handleLogoutClick}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </ProfileSection>

      {/* Logo at Bottom */}
      <LogoContainer>
        <LogoImage 
          src={mode === 'dark' ? logoWhiteImage : logoBlackImage} 
          alt="myautoIQ" 
        />
      </LogoContainer>

      {/* Debug Panel - Only render if enabled and user has role access */}
      {canAccessDebugPanel && (
        <DebugPanelMAIQ
          open={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
        />
      )}
    </SidebarContainer>
  );
};

